import { useState, useEffect } from "react";
import { apiV1 } from "api-v2";

interface PreflightResponse {
  masked_email: string;
  invitee_name?: string | null;
  company_name?: string | null;
  invited_by_name?: string | null;
  invited_by_email?: string | null;
  invite_expires_at?: string | null;
}

type PreflightErrorCode =
  | "INVITE_TOKEN_INVALID"
  | "INVITE_TOKEN_EXPIRED"
  | "VERIFICATION_LOCKED"
  | "RATE_LIMIT_EXCEEDED";

interface UsePreflightInviteTokenResult {
  maskedEmail: string | null;
  inviteeName: string | null;
  companyName: string | null;
  invitedByName: string | null;
  invitedByEmail: string | null;
  inviteExpiresAt: string | null;
  isLoading: boolean;
  isExpired: boolean;
  errorCode: PreflightErrorCode | null;
}

/**
 * Cheap, idempotent token check on mount. Calls the preflight endpoint —
 * which validates the token and returns the masked email WITHOUT issuing a
 * verification code or sending an email. Safe under React StrictMode
 * double-mount, refresh, or browser retry.
 *
 * The actual code dispatch is handled by useIssueVerificationCodeMutation,
 * which the user triggers from the email-confirmation step.
 */
export const usePreflightInviteToken = (token: string | null): UsePreflightInviteTokenResult => {
  const [maskedEmail, setMaskedEmail] = useState<string | null>(null);
  const [inviteeName, setInviteeName] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState<string | null>(null);
  const [invitedByName, setInvitedByName] = useState<string | null>(null);
  const [invitedByEmail, setInvitedByEmail] = useState<string | null>(null);
  const [inviteExpiresAt, setInviteExpiresAt] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [errorCode, setErrorCode] = useState<PreflightErrorCode | null>(null);

  useEffect(() => {
    if (!token) {
      setIsExpired(true);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    apiV1
      .post<PreflightResponse>("auth/invite/preflight-link", { token })
      .then((res) => {
        if (!cancelled) {
          setMaskedEmail(res.data.masked_email);
          setInviteeName(res.data.invitee_name ?? null);
          setCompanyName(res.data.company_name ?? null);
          setInvitedByName(res.data.invited_by_name ?? null);
          setInvitedByEmail(res.data.invited_by_email ?? null);
          setInviteExpiresAt(res.data.invite_expires_at ?? null);
        }
      })
      .catch((err) => {
        if (cancelled) {
          return;
        }
        const code: PreflightErrorCode =
          err?.response?.data?.code ?? "INVITE_TOKEN_INVALID";
        if (code === "INVITE_TOKEN_EXPIRED" || code === "INVITE_TOKEN_INVALID") {
          setIsExpired(true);
        }
        setErrorCode(code);
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  return {
    maskedEmail,
    inviteeName,
    companyName,
    invitedByName,
    invitedByEmail,
    inviteExpiresAt,
    isLoading,
    isExpired,
    errorCode,
  };
};
